import React from 'react';
import { Modal, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { FaCheck, FaTimes, FaExclamationTriangle, FaCodeBranch } from 'react-icons/fa';

/**
 * ConfirmationModal Component
 * 
 * Modal that displays lender criteria evaluation before submission
 * Shows matched, missed, and incomplete criteria for user confirmation
 * Supports both simple and conditional (advanced) criteria display
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onHide - Function to call when modal is closed
 * @param {Function} props.onConfirm - Function to call when user confirms submission
 * @param {string} props.lenderName - Name of the lender
 * @param {Array} props.matchedCriteria - Array of criteria that are met
 * @param {Array} props.missedCriteria - Array of criteria that are not met
 * @param {Array} props.incompleteCriteria - Array of criteria with missing data
 * @param {boolean} props.isSubmitting - Whether submission is in progress
 * @param {boolean} props.hasDisqualifyingCriteria - Whether there are disqualifying missed criteria
 * @param {Object} props.advancedCriteriaResults - Optional results from advanced criteria evaluation
 * @returns {JSX.Element} ConfirmationModal component
 */
const ConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  lenderName,
  matchedCriteria = [],
  missedCriteria = [],
  incompleteCriteria = [],
  isSubmitting = false,
  hasDisqualifyingCriteria = false,
  advancedCriteriaResults = null,
  isAutoDeclineBypass = false,
  lenderNormallyBlocksOverride = false  // NEW: Show extra warning if lender normally blocks
}) => {
  
  // Add custom CSS for proper centering
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .compact-confirmation-modal .modal-dialog {
        display: flex !important;
        align-items: center !important;
        min-height: calc(100vh - 1rem) !important;
      }
      .compact-confirmation-modal .modal-content {
        width: 100% !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Format criterion value for display
  const formatCriterionValue = (criterion) => {
    if (criterion.operator === 'is_not' && Array.isArray(criterion.value)) {
      return `Not: ${criterion.value.join(', ')}`;
    }
    
    if (criterion.operator === 'is_one_of' && Array.isArray(criterion.value)) {
      return `One of: ${criterion.value.join(', ')}`;
    }
    
    if (criterion.operator === 'older_than_months') {
      const months = criterion.value;
      return `Older than ${months} ${months === 1 ? 'month' : 'months'}`;
    }
    
    // Format operator for display
    const operatorMap = {
      '>=': '≥',
      '<=': '≤',
      '>': '>',
      '<': '<',
      '=': '=',
      'contains': 'contains',
      'is_not': 'is not',
      'is_one_of': 'is one of'
    };
    
    const displayOperator = operatorMap[criterion.operator] || criterion.operator;
    const valueDisplay = Array.isArray(criterion.value) ? criterion.value.join(', ') : criterion.value;
    return `${displayOperator} ${valueDisplay}`;
  };
  
  // Format actual value for display
  const formatActualValue = (actualValue) => {
    if (actualValue === undefined || actualValue === null || actualValue === '') {
      return 'Not provided';
    }
    
    if (Array.isArray(actualValue)) {
      return actualValue.join(', ');
    }
    
    return String(actualValue);
  };
  
  // Format condition description for display
  const formatConditionForDisplay = (conditionDescription) => {
    if (!conditionDescription) return '';
    // Make it more readable
    return conditionDescription
      .replace('is one of', 'is')
      .replace(/\[([^\]]+)\]/g, '$1'); // Remove brackets around arrays
  };
  
  // Render criterion item with optional conditional context
  const renderCriterionItem = (criterion, type, showConditionContext = true) => {
    const iconMap = {
      matched: <FaCheck className="text-success me-2" />,
      missed: <FaTimes className="text-danger me-2" />,
      incomplete: <FaExclamationTriangle className="text-warning me-2" />
    };
    
    const bgColorMap = {
      matched: 'bg-success bg-opacity-10 border-success',
      missed: 'bg-danger bg-opacity-10 border-danger',
      incomplete: 'bg-warning bg-opacity-10 border-warning'
    };
    
    const hasConditionalContext = criterion.conditionalContext && showConditionContext;
    
    return (
      <div 
        key={`${criterion.fieldId}-${criterion.operator}-${criterion.conditionalContext?.ruleId || 'simple'}-${Math.random()}`} 
        className={`p-2 mb-2 border rounded ${bgColorMap[type]}`}
        style={{ 
          width: '100%',
          borderLeft: hasConditionalContext ? '3px solid #0d6efd' : undefined
        }}
      >
        <div className="d-flex align-items-start">
          {iconMap[type]}
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between align-items-start">
              <div className="fw-bold small">
                {criterion.fieldName}
                {criterion.required && <span className="text-danger">*</span>}
              </div>
            <div className="small text-muted" style={{ fontSize: '0.75rem' }}>
                {formatCriterionValue(criterion)}
              </div>
            </div>
            {type !== 'incomplete' && (
              <div className="small" style={{ fontSize: '0.75rem', color: type === 'matched' ? '#198754' : '#dc3545' }}>
                Current: {formatActualValue(criterion.actualValue)}
              </div>
            )}
            {/* Show conditional context if present - shows WHY this criterion applies */}
            {hasConditionalContext && (
              <div className="small mt-1 p-1 rounded" style={{ fontSize: '0.7rem', backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                <FaCodeBranch className="me-1 text-primary" />
                <span className="text-primary">
                  IF {formatConditionForDisplay(criterion.conditionalContext.conditionDescription)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Separate simple and conditional criteria for cleaner display
  const separateCriteria = (criteria) => {
    const simple = [];
    const conditional = [];
    
    criteria.forEach(criterion => {
      if (criterion.conditionalContext) {
        conditional.push(criterion);
      } else {
        simple.push(criterion);
      }
    });
    
    return { simple, conditional };
  };
  
  // Render all criteria (simple first, then conditional with context)
  const renderAllCriteria = (criteria, type) => {
    const { simple, conditional } = separateCriteria(criteria);
    
    return (
      <>
        {/* Render simple criteria first */}
        {simple.map(criterion => renderCriterionItem(criterion, type, false))}
        
        {/* Render conditional criteria with context */}
        {conditional.length > 0 && (
          <>
            {simple.length > 0 && <hr className="my-2" style={{ opacity: 0.2 }} />}
            <div className="small text-muted mb-1" style={{ fontSize: '0.7rem' }}>
              <FaCodeBranch className="me-1" /> Conditional Criteria:
            </div>
            {conditional.map(criterion => renderCriterionItem(criterion, type, true))}
          </>
        )}
      </>
    );
  };
  
  const totalCriteria = matchedCriteria.length + missedCriteria.length + incompleteCriteria.length;
  const hasAnyCriteria = totalCriteria > 0;
  
  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered 
      dialogClassName="compact-confirmation-modal"
    >
      <div style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <Modal.Header closeButton className="py-2" style={{ flexShrink: 0 }}>
          <Modal.Title className="h6 mb-0">
            Confirm Submission to {lenderName}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto', flex: 1 }}>
        {/* Auto-Decline Bypass Warning - Show prominently at top */}
        {isAutoDeclineBypass && (
          <Alert variant={lenderNormallyBlocksOverride ? "danger" : "warning"} className="mb-3">
            <Alert.Heading className="h6">
              <FaExclamationTriangle className="me-2" />
              Auto-Decline Warning
            </Alert.Heading>
            <p className="mb-2">
              This deal does not meet required criteria for <strong>{lenderName}</strong> and would normally be auto-declined.
            </p>
            {lenderNormallyBlocksOverride && (
              <p className="mb-2 text-danger fw-bold">
                ⚠️ Note: This lender normally blocks all auto-decline overrides. You are only able to submit because you have special bypass permissions.
              </p>
            )}
            <hr />
            <p className="mb-0 fw-bold">
              Please get approval from your sales manager before proceeding with this submission.
            </p>
          </Alert>
        )}
        
        {!hasAnyCriteria ? (
          <Alert variant="info">
            <Alert.Heading>No Criteria Configured</Alert.Heading>
            <p className="mb-0">
              This lender does not have any matching criteria configured. 
              The submission will proceed without criteria evaluation.
            </p>
          </Alert>
        ) : (
          <>
            <p className="mb-3 small">
              Please verify that this deal meets the following business and personal criteria before submitting to <strong>{lenderName}</strong>:
            </p>
            
            {hasDisqualifyingCriteria && !isAutoDeclineBypass && (
              <Alert variant="danger" className="mb-3 py-2">
                <div className="fw-bold small">⚠️ Disqualifying Criteria Detected</div>
                <div className="mb-0 small" style={{ fontSize: '0.8rem' }}>
                  This deal does not meet some required criteria for this lender. 
                  Submitting may result in automatic rejection.
                </div>
              </Alert>
            )}
            
            {/* Missed Criteria - Show first so users see what's wrong */}
            {missedCriteria.length > 0 && (
              <div className="mb-3">
                <div className="text-danger mb-2 fw-bold small">
                  <FaTimes className="me-1" style={{ fontSize: '0.8rem' }} />
                  Missed Criteria ({missedCriteria.length})
                </div>
                {renderAllCriteria(missedCriteria, 'missed')}
              </div>
            )}
            
            {/* Incomplete Criteria - Show second so users see what's missing */}
            {incompleteCriteria.length > 0 && (
              <div className="mb-3">
                <div className="text-warning mb-2 fw-bold small">
                  <FaExclamationTriangle className="me-1" style={{ fontSize: '0.8rem' }} />
                  Pending Input ({incompleteCriteria.length})
                </div>
                <div className="small text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                  These criteria cannot be evaluated because the required information has not been provided yet.
                </div>
                {renderAllCriteria(incompleteCriteria, 'incomplete')}
              </div>
            )}
            
            {/* Matched Criteria - Show last as confirmation */}
            {matchedCriteria.length > 0 && (
              <div className="mb-3">
                <div className="text-success mb-2 fw-bold small">
                  <FaCheck className="me-1" style={{ fontSize: '0.8rem' }} />
                  Matched Criteria ({matchedCriteria.length})
                </div>
                {renderAllCriteria(matchedCriteria, 'matched')}
              </div>
            )}
            
            {/* Skipped Conditional Rules */}
            {advancedCriteriaResults?.skippedRules?.length > 0 && (
              <div className="mb-3">
                <div className="text-secondary mb-2 fw-bold small">
                  <FaCodeBranch className="me-1" style={{ fontSize: '0.8rem' }} />
                  Conditional Rules Not Applied ({advancedCriteriaResults.skippedRules.length})
                </div>
                <div className="small text-muted mb-2" style={{ fontSize: '0.7rem' }}>
                  These rules have conditions that weren't met.
                </div>
                {advancedCriteriaResults.skippedRules.map((rule, index) => (
                  <div 
                    key={index} 
                    className="p-2 mb-1 border rounded"
                    style={{ 
                      backgroundColor: '#f9fafb',
                      borderColor: '#e5e7eb',
                      borderLeft: '3px solid #6b7280'
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <FaCodeBranch className="text-secondary me-2" style={{ fontSize: '0.7rem' }} />
                      <span className="fw-medium small">{rule.ruleName}</span>
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                      IF {rule.conditionDescription}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.65rem' }}>
                      → {rule.thenCriteriaCount} criteria would apply if met
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Summary */}
            <div className="border-top pt-2 mt-2">
              <div className="row text-center">
                <div className="col-4">
                  <div className="text-success fw-bold small">{matchedCriteria.length}</div>
                  <div className="small text-muted" style={{ fontSize: '0.75rem' }}>Matched</div>
                </div>
                <div className="col-4">
                  <div className="text-danger fw-bold small">{missedCriteria.length}</div>
                  <div className="small text-muted" style={{ fontSize: '0.75rem' }}>Missed</div>
                </div>
                <div className="col-4">
                  <div className="text-warning fw-bold small">{incompleteCriteria.length}</div>
                  <div className="small text-muted" style={{ fontSize: '0.75rem' }}>Pending</div>
                </div>
              </div>
            </div>
          </>
        )}
        </Modal.Body>
        
        <Modal.Footer className="py-2" style={{ flexShrink: 0 }}>
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting} size="sm">
            Cancel
          </Button>
          <Button 
            variant={isAutoDeclineBypass ? "warning" : (hasDisqualifyingCriteria ? "danger" : "primary")} 
            onClick={onConfirm}
            disabled={isSubmitting}
            size="sm"
          >
            {isSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                Submitting...
              </>
            ) : (
              <>
                {isAutoDeclineBypass ? 'I Have Approval - Submit Anyway' : (hasDisqualifyingCriteria ? 'Submit Anyway' : 'Confirm Submission')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
